import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CalendarCheck,
  Clock,
  Images,
  Layers,
  Palette,
  Printer,
  Ruler,
  ShieldCheck,
  Sparkles,
  UserRound,
  Wand2,
  X,
} from 'lucide-react';
import { MODELOS, ROTULO_CATEGORIA, TEMAS, TIPOS_CADERNO, FORMATOS, modelosPorCategoria } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LogoCompleta } from '@/components/Logo';
import { MotivosFlutuantes } from '@/components/MotivosFlutuantes';
import { useAuth } from '@/auth/AuthContext';

/**
 * A página inicial contada como uma **história**.
 *
 * A ordem não é a de um folheto de recursos, é a de uma narrativa, e cada capítulo
 * tem uma função:
 *
 * 1. **a ideia** — o herói é quem chega: quem quer uma agenda bonita e não é
 *    designer;
 * 2. **o caminho de sempre** — o obstáculo dito em voz alta (margem, cor, o arquivo
 *    que a gráfica devolve). Quem já tentou reconhece a própria história aqui, e é
 *    isso que faz o resto ser lido;
 * 3. **a virada** — o gerador assume o trabalho chato, e a **prova** vem junto
 *    (`NUMEROS`): o que já está pronto, contado no motor;
 * 4. **o que deixa de ser o seu problema** — cada escolha da pessoa, e o que o
 *    gerador resolve por trás dela (`RECURSOS`);
 * 5. **a sua noite** — a mesma jornada em quatro momentos, com hora marcada: é o
 *    capítulo que responde "quanto trabalho me dá?";
 * 6. **duas histórias** — os dois produtos, um para quem vende papelaria e outro
 *    para quem escreve (`PRODUTOS`);
 * 7. **o primeiro passo** — o convite, com a garantia do que fica guardado.
 *
 * Três regras de tom, que vêm do que já está decidido para o site ([[Aparência do
 * site]]): a interface continua discreta e a alegria vem do desenho e do tom; os
 * números são **conferidos no motor**, porque número de vitrine que não bate com o
 * produto é promessa quebrada na primeira tela; e nada é prometido que o produto não
 * faça — a demonstração mostra as páginas de verdade, com marca d'água, e o editor
 * de documentos depende de liberação do administrador.
 */

/**
 * Os tons dos enfeites dos capítulos, em ciclo.
 *
 * Um tom por bloco, girando pela lista: dá cor sem transformar a página num
 * arco-íris e sem tirar a leitura de foco.
 */
const TONS = [
  'var(--cor-alegre-4)',
  'var(--cor-alegre-3)',
  'var(--cor-alegre-1)',
  'var(--cor-alegre-2)',
  'var(--cor-alegre-5)',
  'var(--cor-alegre-4)',
  'var(--cor-alegre-1)',
  'var(--cor-alegre-3)',
];

/**
 * Os números que respondem "isso existe mesmo?" logo depois da virada.
 *
 * São contagens conferidas no motor (`MODELOS`, `FORMATOS`, `TEMAS`,
 * `TIPOS_CADERNO`): promessa só vale acompanhada do que já está pronto.
 */
const NUMEROS = [
  { valor: `${MODELOS.length}`, rotulo: 'modelos prontos', tom: 'var(--cor-alegre-4)' },
  { valor: `${FORMATOS.length}`, rotulo: 'formatos de folha', tom: 'var(--cor-alegre-3)' },
  { valor: `${Object.keys(TEMAS).length}`, rotulo: 'temas harmonizados', tom: 'var(--cor-alegre-1)' },
  { valor: `${TIPOS_CADERNO.length}`, rotulo: 'tipos de caderno', tom: 'var(--cor-alegre-5)' },
];

/**
 * O caminho de sempre: o que costuma dar errado antes de existir o gerador.
 *
 * É o capítulo do obstáculo, e cada linha é uma coisa que já aconteceu com quem
 * montou agenda à mão — por isso ele vem antes de qualquer recurso.
 */
const OBSTACULOS = [
  'A margem muda de página para página, e a lombada come o texto.',
  'As cores brigam entre si, porque ninguém escolheu a paleta inteira.',
  'O arquivo volta da gráfica: “está fora do tamanho da folha”.',
  'A diagramação come o fim de semana, e não a ideia.',
];

/**
 * O que cada escolha sua economiza.
 *
 * Cada cartão é a mesma história em uma frase: **você escolhe uma coisa** (o título)
 * e o gerador resolve o resto (a descrição) — é o capítulo em que o trabalho chato
 * troca de dono.
 */
const RECURSOS = [
  {
    icone: Ruler,
    titulo: 'Você escolhe a folha: A4, A5 ou A6',
    texto:
      'Margens de encadernação, grid modular e altura de pauta saem calculados para aquele tamanho — e para os outros formatos, do Slim ao Universitário. Nada de layout perdido no papel.',
  },
  {
    icone: CalendarCheck,
    titulo: 'Você escolhe os dias da semana',
    texto:
      'Segunda a sexta, segunda a sábado, semana completa ou só fim de semana — com um ou dois dias por página. A agenda datada calcula a paginação inteira sozinha.',
  },
  {
    icone: Palette,
    titulo: 'Você escolhe uma cor',
    texto:
      'O gerador cria a paleta inteira a partir dela: harmonia (análoga, complementar, tríade…), neutros tingidos e contraste WCAG garantido. A cor que combina deixa de ser uma aposta.',
  },
  {
    icone: BookOpen,
    titulo: 'Você escolhe o que se lê em cada dia',
    texto:
      'Devocional, motivacional, curiosidades (“Você sabia?”, com um fato do mundo e uma sugestão para experimentar hoje) ou página totalmente livre. O conteúdo é original e determinístico: o mesmo dia recebe sempre o mesmo texto.',
  },
  {
    icone: Images,
    titulo: 'Você escolhe o acabamento',
    texto:
      'Doze temas harmonizados, dez ornamentos — inclusive papel pontilhado, quadriculado, ondinhas e confete, que decoram a folha inteira — e a sua própria imagem de fundo, com véu de leitura ajustável.',
  },
  {
    icone: Printer,
    titulo: 'Você escolhe “salvar em PDF”',
    texto:
      'Páginas em milímetros, sem margem extra, com quebra correta e cor exata: o arquivo sai direto do navegador, no tamanho da folha que a gráfica pede.',
  },
];

/**
 * A mesma jornada em quatro momentos.
 *
 * As horas são de um exemplo (uma noite), e não uma promessa de relógio: o que o
 * produto promete é "em minutos", da ideia ao PDF. O detalhe do que acontece em cada
 * momento é o que faz a pessoa se ver fazendo.
 */
const MOMENTOS = [
  {
    quando: '19h00',
    titulo: 'A estrutura',
    texto: 'Você diz o tamanho, o período e quantos dias cabem em cada página.',
  },
  {
    quando: '19h05',
    titulo: 'A identidade',
    texto: 'Escolhe uma cor, um tema, um ornamento e a fonte. O resto vem junto.',
  },
  {
    quando: '19h20',
    titulo: 'Os detalhes',
    texto: 'Liga os quadros do dia e os cadernos, põe os seus dados e sua imagem.',
  },
  {
    quando: '19h35',
    titulo: 'O PDF',
    texto: 'Você folheia página a página, imprime, salva e manda para a gráfica.',
  },
];

export function Inicio() {
  const { usuario } = useAuth();

  /*
   * O editor de documentos é liberado **pelo administrador**, então o botão do
   * cartão não pode prometer a área a quem ainda não tem a liberação: para o
   * visitante ele leva ao cadastro, e para quem já tem, à área.
   */
  const podeEscrever = Boolean(usuario?.editorDeDocumentos || usuario?.admin);
  const PRODUTOS = [
    {
      icone: CalendarCheck,
      tom: 'var(--cor-alegre-4)',
      titulo: 'A história de quem vende papelaria',
      texto:
        'A ideia é uma agenda que ninguém tem. O gerador diagrama, calcula o grid, harmoniza as cores e entrega o PDF pronto para imprimir — ou para vender.',
      itens: [
        'A4, A5, A6, Slim, Quadrado, Big e Universitário',
        'Conteúdo devocional, motivacional, curiosidades e mais',
        'Cadernos de contatos, finanças, treino, receitas, partitura...',
      ],
      rotulo: 'Montar um miolo',
      destino: '/criar',
    },
    {
      icone: BookOpen,
      tom: 'var(--cor-alegre-1)',
      titulo: 'A história de quem escreve',
      texto:
        'A ideia é um trabalho, um artigo ou um livro. O editor já vem com a formatação da ABNT, capa, sumário, capítulos numerados, cabeçalho e número de página.',
      itens: [
        'Formatação da ABNT pronta (margens, citações, referências)',
        'Livro em A4 ou A5, com cabeçalho, filete e capitular',
        'Acesso gratuito para conhecer, e plano só deste editor quando quiser',
      ],
      rotulo: podeEscrever ? 'Abrir o editor' : 'Ver a área de quem escreve',
      /* Sem acesso, o caminho é a área do produto — onde se conhece de graça. */
      destino: podeEscrever ? '/documentos' : '/escrever',
    },
  ];

  return (
    <div className="space-y-16 py-4">
      {/*
        Capítulo 1 — a ideia (o herói).

        `isolate` + `-z-10` prendem o brilho **dentro** desta seção: sem o
        isolamento, um elemento de camada negativa cairia atrás do fundo da página e
        sumiria. Os desenhos e o texto ficam numa camada por cima, para os enfeites
        passarem por trás da leitura e não por cima dela.
      */}
      <section
        className="secao-forte relative isolate mx-auto flex max-w-3xl flex-col items-center gap-6 rounded-3xl px-5 py-10 text-center shadow-sm"
        style={{ '--tom': 'var(--cor-alegre-4)' } as React.CSSProperties}
      >
        <MotivosFlutuantes />
        {/*
          O brilho: no máximo a largura da seção. Com `max-w-[110%]` ele passava
          5 px de cada lado no celular e a página ganhava uma rolagem lateral de
          nada — o enfeite não paga esse preço.
        */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 left-1/2 -z-10 h-72 w-[34rem] max-w-full -translate-x-1/2 rounded-full opacity-70 blur-3xl"
          style={{
            backgroundImage:
              'radial-gradient(closest-side, color-mix(in oklab, var(--cor-alegre-2) 30%, transparent), transparent), radial-gradient(closest-side at 70% 60%, color-mix(in oklab, var(--cor-alegre-1) 26%, transparent), transparent)',
          }}
        />

        <div className="relative z-10 flex flex-col items-center gap-6">
          <LogoCompleta className="max-w-[300px] sm:max-w-[380px]" />
          <Badge tom="principal" className="mx-auto">
            <Sparkles className="size-3" />
            Sem instalar nada · o PDF sai do navegador
          </Badge>
          <h1 className="text-4xl font-semibold leading-[1.1] tracking-tight sm:text-5xl">
            A sua ideia de agenda não precisa de um projeto gráfico para existir
          </h1>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground">
            Você escolhe o tamanho, os dias, as cores e o que entra em cada página. O gerador faz a
            diagramação, o grid, o contraste e a paginação — e entrega o arquivo pronto para imprimir ou
            vender.
          </p>
          <p className="text-sm font-medium">
            Começa com uma ideia. Termina com o miolo na mão da gráfica.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild tamanho="grande">
              <Link to="/criar">
                Começar meu miolo
                <ArrowRight />
              </Link>
            </Button>
            {usuario ? (
              <Button asChild variant="contorno" tamanho="grande">
                <Link to="/meus-miolos">Ver meus miolos</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="contorno" tamanho="grande">
                  <Link to="/demo">Testar a demonstração</Link>
                </Button>
                <Button asChild variant="fantasma" tamanho="grande">
                  <Link to="/entrar">Já tenho conta</Link>
                </Button>
              </>
            )}
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Criar conta é rapidinho: nome, e-mail e senha. Quer só espiar primeiro? A demonstração mostra as
            páginas de verdade, com a marca “cópia não autorizada”.
          </p>

          {/*
            As etiquetas de cor: cada uma pinta o ponto e a borda com um tom de
            enfeite, para o alto da página ter vida sem mexer no texto.
          */}
          <ul className="flex flex-wrap items-center justify-center gap-2">
            {[
              { texto: 'Imprime no navegador', tom: 'var(--cor-alegre-5)' },
              { texto: 'Conteúdo original por dia', tom: 'var(--cor-alegre-2)' },
              { texto: 'Pronto para vender', tom: 'var(--cor-alegre-1)' },
            ].map((etiqueta) => (
              <li
                key={etiqueta.texto}
                className="cartao-tom flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium"
                style={{ '--tom': etiqueta.tom } as React.CSSProperties}
              >
                <span className="size-1.5 rounded-full" style={{ background: etiqueta.tom }} />
                {etiqueta.texto}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Capítulo 2 — o caminho de sempre (o obstáculo, dito em voz alta). */}
      <section className="mx-auto max-w-3xl space-y-4">
        <RotuloDoCapitulo numero={2} titulo="O caminho de sempre" />
        <h2 className="text-2xl font-semibold tracking-tight">
          Se você já tentou montar uma agenda, conhece esta lista
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Ela não é sobre falta de ideia. É sobre o trabalho que aparece <strong>depois</strong> da ideia —
          e que come o tempo, a paciência e o fim de semana de quem só queria ver a agenda pronta.
        </p>
        <ul className="grid gap-2 rounded-xl border border-dashed border-border p-5 sm:grid-cols-2">
          {OBSTACULOS.map((linha) => (
            <li
              key={linha}
              className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground"
            >
              <X className="mt-1 size-3.5 shrink-0 opacity-60" />
              {linha}
            </li>
          ))}
        </ul>
      </section>

      {/* Capítulo 3 — a virada, com a prova junto. */}
      <section className="space-y-6">
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <div className="flex justify-center">
            <RotuloDoCapitulo numero={3} titulo="A virada" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">
            Nada disso é o seu trabalho. O seu trabalho é a ideia.
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            O layout aqui é calculado em milímetros — margem, grade, pauta e encadernação conversam entre si
            antes de qualquer coisa aparecer na tela. Você faz as escolhas que dão personalidade; a conta
            fica com o gerador.
          </p>
        </div>

        {/* A prova: o que já está pronto, contado no motor. */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {NUMEROS.map((numero) => (
            <div
              key={numero.rotulo}
              className="painel-tom faixa-tom rounded-xl border p-5 text-center"
              style={{ '--tom': numero.tom } as React.CSSProperties}
            >
              <p className="numero text-3xl font-semibold tracking-tight">{numero.valor}</p>
              <p className="mt-1 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                {numero.rotulo}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Capítulo 4 — o que deixa de ser o seu problema. */}
      <section className="space-y-6">
        <div className="mx-auto max-w-3xl space-y-3 text-center">
          <div className="flex justify-center">
            <RotuloDoCapitulo numero={4} titulo="O que deixa de ser o seu problema" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">
            Cada escolha sua é uma decisão técnica já tomada
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Em cada cartão: no título, o que você escolhe; na descrição, o que o gerador resolve para que
            essa escolha não vire problema no papel.
          </p>
        </div>
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {RECURSOS.map(({ icone: Icone, titulo, texto }, indice) => {
            const tom = TONS[indice % TONS.length];
            return (
              <Card
                key={titulo}
                className="cartao-tom faixa-tom transition-transform hover:-translate-y-0.5"
                style={{ '--tom': tom } as React.CSSProperties}
              >
                <CardHeader>
                  <span
                    className="flex size-9 items-center justify-center rounded-md"
                    style={{ background: `color-mix(in oklab, ${tom} 14%, transparent)`, color: tom }}
                  >
                    <Icone className="size-4" />
                  </span>
                  <CardTitle className="pt-1">{titulo}</CardTitle>
                  <CardDescription>{texto}</CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Capítulo 5 — a sua noite: a mesma jornada em quatro momentos. */}
      <section className="space-y-6">
        <div className="mx-auto max-w-3xl space-y-3 text-center">
          <div className="flex justify-center">
            <RotuloDoCapitulo numero={5} titulo="A sua noite" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">Da ideia ao PDF, numa noite</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            A sua noite pode ser assim — as horas são de um exemplo, para você ver onde o trabalho fica:
          </p>
        </div>
        <ol className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {MOMENTOS.map((item, indice) => {
            const tom = TONS[indice % TONS.length];
            return (
              <li
                key={item.quando}
                className="cartao-tom rounded-lg border p-5"
                style={{ '--tom': tom } as React.CSSProperties}
              >
                {/* O relógio dá o passo do tempo; o tom, a cor do capítulo. */}
                <span
                  className="selo-tom inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                  style={{ '--tom': tom } as React.CSSProperties}
                >
                  <Clock className="size-3.5" />
                  {item.quando}
                </span>
                <h3 className="mt-3 text-sm font-semibold">{item.titulo}</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.texto}</p>
              </li>
            );
          })}
        </ol>

        {/*
          Nem toda história começa da folha em branco: quem não quer escolher tudo
          parte de uma receita pronta — é o atalho honesto, e ele fica aqui, quando a
          pessoa já sabe o que o assistente resolve.
        */}
        <div
          className="cartao-tom faixa-tom space-y-6 rounded-xl border p-8"
          style={{ '--tom': 'var(--cor-alegre-4)' } as React.CSSProperties}
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-2">
              <Badge tom="principal">
                <Wand2 className="size-3" />
                {MODELOS.length} modelos prontos
              </Badge>
              <h3 className="text-xl font-semibold tracking-tight">Ou comece de uma receita pronta</h3>
              <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
                Agenda devocional, planner de trabalho, agenda do dentista, diário de viagem, plano de
                leitura da Bíblia — cada modelo já vem com formato, período, cores, tema, quadros do dia e
                cadernos. Veja a prévia real e personalize o que quiser.
              </p>
            </div>
            <Button asChild variant="contorno" tamanho="grande">
              <Link to="/modelos">
                <Layers />
                Ver modelos prontos
                <ArrowRight />
              </Link>
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {modelosPorCategoria().map(({ categoria, modelos }) => (
              <span
                key={categoria}
                className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground"
              >
                {ROTULO_CATEGORIA[categoria]} ({modelos.length})
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Capítulo 6 — duas histórias, um lugar só (os dois produtos). */}
      <section className="space-y-6">
        <div className="mx-auto max-w-3xl space-y-3 text-center">
          <div className="flex justify-center">
            <RotuloDoCapitulo numero={6} titulo="Duas histórias, um lugar só" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">
            A sua história começa por um destes dois caminhos
          </h2>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {PRODUTOS.map((produto) => (
            <section
              key={produto.titulo}
              className="cartao-tom faixa-tom flex flex-col gap-3 rounded-xl border p-6"
              style={{ '--tom': produto.tom } as React.CSSProperties}
            >
              <span
                className="flex size-10 items-center justify-center rounded-lg"
                style={{
                  background: `color-mix(in oklab, ${produto.tom} 16%, transparent)`,
                  color: produto.tom,
                }}
              >
                <produto.icone className="size-5" />
              </span>
              <h3 className="text-lg font-semibold tracking-tight">{produto.titulo}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{produto.texto}</p>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                {produto.itens.map((linha) => (
                  <li key={linha} className="flex items-start gap-2">
                    <span
                      className="mt-1 size-1.5 shrink-0 rounded-full"
                      style={{ background: produto.tom }}
                    />
                    {linha}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-3">
                <Button asChild variant="contorno" tamanho="pequeno">
                  <Link to={produto.destino}>
                    {produto.rotulo}
                    <ArrowRight />
                  </Link>
                </Button>
              </div>
            </section>
          ))}
        </div>
      </section>

      {/* Capítulo 7 — o primeiro passo (o convite, com o que fica guardado). */}
      <section className="painel-degrade rounded-2xl p-8 text-center shadow-lg">
        <ShieldCheck className="mx-auto size-7" />
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
          7 · O primeiro passo
        </p>
        <h2 className="mt-2 text-xl font-semibold tracking-tight">
          A ideia você já tem. Falta só começar
        </h2>
        <p className="texto-suave mx-auto mt-2 max-w-xl text-sm leading-relaxed">
          Cada miolo guarda a configuração completa — formato, paleta, período, dados pessoais e imagem de
          fundo. Você reabre, ajusta, duplica para o ano seguinte e imprime de novo quando quiser.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <Button asChild variant="secundario" className="bg-white text-foreground hover:bg-white/90">
            <Link to={usuario ? '/criar' : '/criar-conta'}>
              <UserRound />
              {usuario ? 'Criar novo miolo' : 'Criar minha conta'}
            </Link>
          </Button>
          <Button asChild variant="fantasma" className="text-white hover:bg-white/15 hover:text-white">
            <Link to="/planos">
              Ver os planos
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

/**
 * O rótulo do capítulo.
 *
 * É ele que dá a sequência da história de longe, sem prender a leitura: número e
 * assunto, em letra pequena, acima do título do capítulo.
 */
function RotuloDoCapitulo({ numero, titulo }: { numero: number; titulo: string }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {numero} · {titulo}
    </p>
  );
}
